/**
 * Sticky Custom Web Component
 * Shared sticky behavior for custom desktop/mobile sticky bars.
 */
class StickyCustom extends HTMLElement {
  constructor() {
    super();

    this.config = {
      position: "bottom",
      visibility: "always",
      scrollPercentage: 10,
      docked: false,
      hideAtBottom: false,
      deviceType: "desktop",
    };

    this.scrollTicking = false;
    this.dockedScrollTicking = false;
    this.dockedOriginalTop = null;
    this.dockedHeight = 0;
    this.dockScrollThreshold = 0;
    this._lastPlaceholderDisplay = null;

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
  }

  connectedCallback() {
    this.parseConfig();
    this.applyFullWidthActionSizing();
    this.setupEventListeners();

    if (this.config.docked) {
      this.setupDocking();
    }

    this.updateVisibility();
    // Some nested blocks initialize after first paint.
    requestAnimationFrame(() => this.applyFullWidthActionSizing());
    setTimeout(() => this.applyFullWidthActionSizing(), 150);
  }

  disconnectedCallback() {
    this.removeEventListeners();

    if (this.dockedPlaceholder && this.dockedPlaceholder.parentNode) {
      this.dockedPlaceholder.parentNode.removeChild(this.dockedPlaceholder);
      this.dockedPlaceholder = null;
    }
  }

  parseConfig() {
    this.config.position = this.dataset.position || "bottom";
    this.config.visibility = this.dataset.visibility || "always";
    this.config.scrollPercentage = parseInt(this.dataset.scrollPercentage, 10);
    if (Number.isNaN(this.config.scrollPercentage)) {
      this.config.scrollPercentage = 10;
    }
    this.config.docked = this.dataset.docked === "true";
    this.config.hideAtBottom = this.dataset.hideAtBottom === "true";
    this.config.deviceType = this.dataset.deviceType || "desktop";
  }

  setupEventListeners() {
    window.addEventListener("scroll", this.onScroll, { passive: true });
    window.addEventListener("resize", this.onResize, { passive: true });
  }

  removeEventListeners() {
    window.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("resize", this.onResize);
  }

  onScroll() {
    if (this.scrollTicking) return;

    this.scrollTicking = true;
    requestAnimationFrame(() => {
      if (this.config.docked) {
        this.updateDockedState();
      }
      this.updateVisibility();
      this.scrollTicking = false;
    });
  }

  onResize() {
    if (this.config.docked) {
      this.refreshDockMetrics();
      this.updateDockedState();
    }
    this.updateVisibility();
  }

  /**
   * Read --header-sticky-offset from the inline style on <html>.
   * This avoids getComputedStyle, which forces a synchronous reflow.
   */
  getHeaderStickyOffset() {
    const raw = document.documentElement.style.getPropertyValue(
      "--header-sticky-offset"
    );
    return parseInt(raw, 10) || 0;
  }

  /**
   * Set placeholder display only when the value actually changes,
   * avoiding unnecessary DOM mutations that cause layout thrashing.
   */
  setPlaceholderDisplay(value) {
    if (this._lastPlaceholderDisplay === value) return;
    this._lastPlaceholderDisplay = value;
    this.dockedPlaceholder.style.display = value;
  }

  setupDocking() {
    this.dockedPlaceholder = document.createElement("div");
    this.dockedPlaceholder.style.cssText =
      "margin:0;padding:0;border:none;visibility:hidden;overflow-anchor:none;display:block;";

    if (this.nextSibling) {
      this.parentNode.insertBefore(this.dockedPlaceholder, this.nextSibling);
    } else {
      this.parentNode.appendChild(this.dockedPlaceholder);
    }

    this.refreshDockMetrics();

    if (this.config.position === "top") {
      this.setPlaceholderDisplay("none");
    } else {
      this.setPlaceholderDisplay("block");
    }

    this.updateDockedState();
  }

  refreshDockMetrics() {
    if (!this.dockedPlaceholder) return;

    const rect = this.getBoundingClientRect();
    this.dockedHeight = Math.max(Math.round(rect.height), this.offsetHeight || 0);
    this.dockedPlaceholder.style.height = `${this.dockedHeight}px`;

    if (this.config.position === "top" && !this.classList.contains("is-stuck")) {
      this.dockedOriginalTop = Math.round(rect.top + window.scrollY);
    }
  }

  updateDockedState() {
    if (!this.config.docked || !this.dockedPlaceholder) return;

    if (this.config.position === "top") {
      this.updateTopDockState();
    } else {
      this.updateBottomDockState();
    }
  }

  updateTopDockState() {
    if (typeof this.dockedOriginalTop !== "number") {
      const rect = this.getBoundingClientRect();
      this.dockedOriginalTop = Math.round(rect.top + window.scrollY);
    }

    const headerOffset = this.getHeaderStickyOffset();
    const stickThreshold = this.dockedOriginalTop - headerOffset;
    const buffer = 5;
    const isStuck = this.classList.contains("is-stuck");

    // For scroll % visibility, only stick when the percentage threshold is met.
    const canStick =
      this.config.visibility === "always" ||
      this.getScrollPercentage() >= this.config.scrollPercentage;

    if (!isStuck && window.scrollY >= stickThreshold && canStick) {
      this.classList.add("is-stuck");
      this.setPlaceholderDisplay("block");
    } else if (isStuck && (window.scrollY < stickThreshold - buffer || !canStick)) {
      this.classList.remove("is-stuck");
      this.setPlaceholderDisplay("none");
    }
  }

  updateBottomDockState() {
    const buffer = 8;

    if (!this.classList.contains("is-docked")) {
      const barVisualTop = window.innerHeight - this.dockedHeight;
      const placeholderRect = this.dockedPlaceholder.getBoundingClientRect();

      // Dock when the placeholder scrolls up to meet the fixed bar's position.
      if (placeholderRect.top <= barVisualTop) {
        this.dockScrollThreshold = window.scrollY;
        this.classList.add("is-docked");
        this.setPlaceholderDisplay("none");
      }
    } else if (window.scrollY < this.dockScrollThreshold - buffer) {
      this.classList.remove("is-docked");
      this.setPlaceholderDisplay("block");
    }
  }

  updateVisibility() {
    const hideNearBottom = this.shouldHideAtBottom();

    if (hideNearBottom) {
      this.classList.add("is-hidden-at-bottom");
    } else {
      this.classList.remove("is-hidden-at-bottom");
    }

    if (this.config.docked) {
      // Bottom dock + scroll %: control visibility of the fixed (non-docked) state.
      // When docked inline, the is-docked CSS rule ensures full opacity.
      // Top dock visibility is handled by updateTopDockState (only sticks when scroll % met).
      if (this.config.position === "bottom" && this.config.visibility === "custom") {
        let shouldShow = this.getScrollPercentage() >= this.config.scrollPercentage;
        if (hideNearBottom) shouldShow = false;
        if (shouldShow) {
          this.classList.add("is-visible");
        } else {
          this.classList.remove("is-visible");
        }
      }
      return;
    }

    let shouldShow = this.config.visibility === "always";

    if (this.config.visibility === "custom") {
      shouldShow = this.getScrollPercentage() >= this.config.scrollPercentage;
    }

    if (hideNearBottom) {
      shouldShow = false;
    }

    if (shouldShow) {
      this.classList.add("is-visible");
    } else {
      this.classList.remove("is-visible");
    }
  }

  shouldHideAtBottom() {
    if (!this.config.hideAtBottom) return false;

    const threshold = 120;
    const scrollBottom = window.scrollY + window.innerHeight;
    const pageBottom = document.documentElement.scrollHeight - threshold;
    return scrollBottom >= pageBottom;
  }

  getScrollPercentage() {
    const doc = document.documentElement;
    const scrollTop = window.scrollY || doc.scrollTop || 0;
    const maxScroll = doc.scrollHeight - window.innerHeight;

    if (maxScroll <= 0) return 100;
    return (scrollTop / maxScroll) * 100;
  }

  applyFullWidthActionSizing() {
    const content = this.querySelector(".custom-sticky__content");
    if (!content) return;

    const layoutMode = content.dataset.layoutMode || "flex";
    if (layoutMode === "three_column_center") {
      return;
    }

    const fullWidthButtons = content.querySelectorAll(
      ".button--full-width"
    );
    if (!fullWidthButtons.length) return;

    fullWidthButtons.forEach((button) => {
      const actionWrapper = button.closest(
        "[class*='link-button-wrapper--'], [class*='buy-button-wrapper--']"
      );
      if (!actionWrapper) return;

      // Flex item is usually Shopify's generated block wrapper.
      const generatedBlockWrapper = actionWrapper.closest("[id^='shopify-block-']");
      const flexItem =
        generatedBlockWrapper && content.contains(generatedBlockWrapper)
          ? generatedBlockWrapper
          : actionWrapper;

      flexItem.style.width = "100%";
      flexItem.style.maxWidth = "100%";
      flexItem.style.flex = "1 1 100%";
      flexItem.style.minWidth = "0";

      actionWrapper.style.width = "100%";
      actionWrapper.style.maxWidth = "100%";
      actionWrapper.style.flex = "1 1 100%";
    });
  }
}

if (!customElements.get("sticky-custom")) {
  customElements.define("sticky-custom", StickyCustom);
}
